import { getStage, LAST_STAGE_ID } from '../data/stages.ts';
import type { FarmBatchReceipt, FarmBatchState, ProgressState, SaveData } from '../model/types.ts';
import { grantXp, resolveHero, scaledXp } from '../sim/build.ts';
import { rollFarmDrops } from '../sim/drops.ts';
import { absorbDrops } from '../sim/gear.ts';
import { applyFightResult } from '../sim/progress.ts';
import { MAX_FARM_STAGE } from '../sim/rateCaps.ts';
import { farmMinGapMs, seedsForFight, watchMs } from './simAuthority.ts';
import { runCombat } from '../sim/combat.ts';

/** Client flush period. The server does not trust this number; it only caps time. */
export const FARM_BATCH_INTERVAL_MS = 45_000;

/**
 * A gap longer than two flush periods cannot bank clears.
 * 90s of the simmed minimum gap is the most one settle will pay.
 */
export const FARM_BATCH_BURST_MS = FARM_BATCH_INTERVAL_MS * 2;

export class FarmBatchError extends Error {
  constructor(message: 'batch_stale' | 'batch_gap' | 'batch_stage' | 'save_invalid') {
    super(message);
  }
}

export function emptyFarmBatch(): FarmBatchState {
  return { seq: 0, settledAtMs: 0, receipt: null };
}

export function readFarmBatch(data: SaveData): FarmBatchState {
  const stored = data.idle.farmBatch;
  if (!stored || typeof stored.seq !== 'number' || typeof stored.settledAtMs !== 'number') return emptyFarmBatch();
  return stored;
}

/**
 * A repeat of a stage the echo already farms.
 * Walls, first clears, and anything above the farm stage stay one validate_clear.
 */
export function isRepeatFarmStage(progress: ProgressState, stageId: number): boolean {
  if (!Number.isInteger(stageId) || stageId < 1 || stageId > MAX_FARM_STAGE) return false;
  const stage = getStage(stageId);
  if (stage.isWall) return false;
  if (stageId > progress.farmStage) return false;
  if (stageId > progress.bestCleared) return false;
  return true;
}

/**
 * Milliseconds the batch may bill.
 * While the session is closed, time after last_active_at belongs to claim_offline.
 * The window is also capped at FARM_BATCH_BURST_MS.
 * A missing anchor (settledAtMs 0) bills nothing, so an old save cannot bank a burst.
 */
export function batchElapsedMs(args: {
  nowMs: number;
  settledAtMs: number;
  sessionClosed: boolean;
  lastActiveAtMs: number | null;
}): number {
  if (!Number.isFinite(args.nowMs) || args.settledAtMs <= 0) return 0;
  let end = args.nowMs;
  if (args.sessionClosed) {
    if (args.lastActiveAtMs === null) return 0;
    end = Math.min(end, args.lastActiveAtMs);
  }
  const raw = Math.max(0, Math.floor(end - args.settledAtMs));
  return Math.min(raw, FARM_BATCH_BURST_MS);
}

/** How many minimum gaps fit in the elapsed window. */
export function farmBatchCap(elapsedMs: number, minGapMs: number): number {
  if (!Number.isFinite(elapsedMs) || elapsedMs < 1) return 0;
  if (!Number.isInteger(minGapMs) || minGapMs < 1) return 0;
  return Math.floor(elapsedMs / minGapMs);
}

export interface ClearWalk {
  level: number;
  xp: number;
  unspent: number;
  awarded: number;
  levelsGained: number;
}

/**
 * Apply `clears` victories one at a time.
 * Each clear uses the falloff of the level it lands on, then `xpToNext`.
 * At the cap the bar stays empty. Further clears still count in `awarded`.
 */
export function walkClearXp(args: {
  level: number;
  xp: number;
  unspent: number;
  clears: number;
  clearXp: number;
  stageId: number;
}): ClearWalk {
  let level = args.level;
  let xp = args.xp;
  let unspent = args.unspent;
  let awarded = 0;
  const clears = Math.max(0, Math.floor(args.clears));
  for (let index = 0; index < clears; index += 1) {
    const gain = level >= 30 ? 0 : scaledXp(args.clearXp, level, args.stageId);
    awarded += gain;
    const granted = grantXp(level, xp, unspent, gain);
    level = granted.level;
    xp = granted.xp;
    unspent = granted.unspent;
  }
  return { level, xp, unspent, awarded, levelsGained: level - args.level };
}

export interface FarmBatchRequest {
  stageId: number;
  count: number;
  seq: number;
  nowMs: number;
  sessionClosed: boolean;
  lastActiveAtMs: number | null;
}

export interface FarmBatchSettled {
  data: SaveData;
  receipt: FarmBatchReceipt;
  replay: boolean;
  /**
   * Present on a new settle. The edge function passes this to record_clear so
   * last_clear_at moves. watchMs is one fight: charging every paid clear would
   * overflow the 60s burst and reject a batch the elapsed cap already allowed.
   */
  rate?: { minGapMs: number; watchMs: number };
}

/**
 * A repeat-farm single is too soon when the shared farm anchor has not moved
 * by a full minimum gap. Returns the wait, or null when the clear may proceed.
 */
export function repeatFarmRetryAfterMs(data: SaveData, nowMs: number, minGapMs: number): number | null {
  if (!Number.isInteger(minGapMs) || minGapMs < 1 || !Number.isFinite(nowMs)) return null;
  const settledAtMs = readFarmBatch(data).settledAtMs;
  if (settledAtMs <= 0) return null;
  const elapsed = nowMs - settledAtMs;
  if (elapsed >= minGapMs) return null;
  return Math.max(1, Math.ceil(minGapMs - elapsed));
}

function anchorEnd(args: { nowMs: number; sessionClosed: boolean; lastActiveAtMs: number | null }): number {
  if (args.sessionClosed && args.lastActiveAtMs !== null) return Math.min(args.nowMs, args.lastActiveAtMs);
  return args.nowMs;
}

/** Stamp the shared clock after a single clear. Does not change the sequence. */
export function noteFarmAnchor(data: SaveData, nowMs: number): SaveData {
  const next = structuredClone(data);
  const prior = readFarmBatch(next);
  next.idle.farmBatch = { ...prior, settledAtMs: nowMs };
  return next;
}

/**
 * One batched settle of an already-farmed stage.
 * The count is a request. Elapsed server time, the simmed gap, and the burst cap decide the pay.
 * The same seq returns the stored receipt and does not pay again.
 */
export function settleFarmBatch(data: SaveData, request: FarmBatchRequest): FarmBatchSettled {
  const prior = readFarmBatch(data);
  if (!Number.isInteger(request.seq) || request.seq < 1) throw new FarmBatchError('save_invalid');
  if (!Number.isInteger(request.count) || request.count < 1 || request.count > 100_000) {
    throw new FarmBatchError('save_invalid');
  }
  if (request.seq === prior.seq) {
    if (!prior.receipt || prior.receipt.seq !== request.seq) throw new FarmBatchError('batch_stale');
    return { data, receipt: prior.receipt, replay: true };
  }
  if (request.seq < prior.seq) throw new FarmBatchError('batch_stale');
  if (request.seq !== prior.seq + 1) throw new FarmBatchError('batch_gap');
  if (!isRepeatFarmStage(data.progress, request.stageId)) throw new FarmBatchError('batch_stage');

  const stage = getStage(request.stageId);
  const seeds = seedsForFight(data.idle.fightSerial, stage.id);
  const hero = resolveHero({
    level: data.hero.level,
    build: data.hero.build,
    gear: data.gear.equipped,
    rules: data.hero.rules,
  });
  const result = runCombat({ hero, stage, seed: seeds.combat, recordLog: false });
  const minGap = farmMinGapMs(result.seconds);
  const elapsed = batchElapsedMs({
    nowMs: request.nowMs,
    settledAtMs: prior.settledAtMs,
    sessionClosed: request.sessionClosed,
    lastActiveAtMs: request.lastActiveAtMs,
  });
  const capped = result.outcome === 'victory' ? farmBatchCap(elapsed, minGap) : 0;
  const paid = Math.min(request.count, capped);
  const next = structuredClone(data);
  next.idle.fightSerial = seeds.nextSerial;
  const rawXp = result.xp;
  const walked = walkClearXp({
    level: next.hero.level,
    xp: next.hero.xp,
    unspent: next.hero.unspentPoints,
    clears: paid,
    clearXp: rawXp,
    stageId: stage.id,
  });
  next.hero.level = walked.level;
  next.hero.xp = walked.xp;
  next.hero.unspentPoints = walked.unspent;
  let dustGained = 0;
  if (paid > 0) {
    next.currencies.gold += result.gold * paid;
    next.currencies.forgeDust += result.dust * paid;
    dustGained += result.dust * paid;
    if (result.dropRolls > 0) {
      const rolled = rollFarmDrops({
        count: result.dropRolls * paid,
        tier: stage.tier,
        pity: next.gear.pity,
        seed: seeds.drops,
        startingId: next.gear.nextItemId,
      });
      next.gear.nextItemId = rolled.nextId;
      next.gear.pity = rolled.pity;
      const bag = absorbDrops(next, rolled.items);
      next.gear.bag = bag.bag;
      next.currencies.gold += bag.gold;
      next.currencies.forgeDust += bag.forgeDust;
      dustGained += bag.forgeDust;
    }
    for (let index = 0; index < paid; index += 1) {
      next.progress = applyFightResult(next.progress, stage, true, LAST_STAGE_ID);
    }
    if (walked.levelsGained > 0) {
      const points = `Level ${next.hero.level}. ${walked.levelsGained} sub-spec point${walked.levelsGained === 1 ? '' : 's'} to spend.`;
      next.progress.notice = next.progress.notice ? `${points} ${next.progress.notice}` : points;
    }
  }
  const receipt: FarmBatchReceipt = {
    seq: request.seq,
    stageId: stage.id,
    paid,
    requested: request.count,
    capped,
    outcome: result.outcome === 'victory' ? 'victory' : 'defeat',
    gold: paid > 0 ? result.gold * paid : 0,
    xp: walked.awarded,
    dust: dustGained,
    dropRolls: paid > 0 ? result.dropRolls * paid : 0,
    levelsGained: walked.levelsGained,
  };
  next.idle.farmBatch = {
    seq: request.seq,
    settledAtMs: anchorEnd(request),
    receipt,
  };
  return {
    data: next,
    receipt,
    replay: false,
    rate: { minGapMs: minGap, watchMs: watchMs(result.seconds) },
  };
}
